/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPlayer from 'react-player/lazy'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi
} from '~/components/UI/shadcn/carousel'

import CarouselControls from '~/components/experimental/CarouselTest/CarouselControls/CarouselControls'

import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { ClientOnly } from 'remix-utils/client-only'
import { Progress } from '~/components/UI/shadcn/progress'
import Image from '~/components/Helpers/Image'
import Container from '~/layouts/Container'
import Full from '~/layouts/Full'
import FormUIProgress from '~/components/UI/Forms/Progress/Progress'
import MarkDown from '~/components/Helpers/MarkDown'

import Play from '~/assets/icons/play'
import MediaPause from '~/assets/icons/media-pause'
import { useQuery } from '@tanstack/react-query'
import { getEntriesByIds } from '~/lib/contensis/misc'
import Loader from '~/components/UI/General/Loader/Loader'

const PlaylistStyles = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d'
    },
    defaultVariant: { backgroundColour: 'ink-blue' }
  }
})

interface VideoPlaylistProps {
  content: {
    heading?: string
    copy?: string
    backgroundColour?: 'ink-blue' | 'mid-blue' | 'light-grey' | 'white'
    reverse?: boolean
    placeholderImage: { altText: string; asset: { sys: { uri: string } } }
    videoTitle?: string
    videos: videoItems[]
  }
  layoutColour?: AllBackgroundColours
  className?: string
}

type videoItems = {
  heading: string
  category: { entryTitle: string }
  duration: string
  youtubeCode: string
}

interface Progress {
  playedSeconds: number
}

interface VideoPlaylistContentProps {
  heading?: string
  copy?: string
  placeholderImage: { altText: string; asset: { sys: { uri: string } } }
  videoTitle?: string
  videos: videoItems[]
  video: string
  setVideo: (code: string) => void
  playing: boolean
  setPlaying: (playing: boolean) => void
  duration: number
  setDuration: (duration: number) => void
  playedSeconds: number
  setPlayedSeconds: (seconds: number) => void
  reverse?: boolean
  backgroundColour?: 'ink-blue' | 'mid-blue' | 'light-grey' | 'white'
  setApi: (api: CarouselApi) => void
  current: number
  setCurrent: (current: number) => void
  count: number
  currentHandler: (value: number) => void
  handlePlayPause: () => void
  handlePlay: () => void
  handlePause: () => void
  handleProgress: (value: Progress) => void
  handleDuration: (value: number) => void
}

const chunkVideos = (videos: videoItems[], size = 3) =>
  Array.from({ length: Math.ceil(videos.length / size) }, (_, i) =>
    videos.slice(i * size, i * size + size)
  )

const VideoPlaylistContent = ({
  heading,
  copy,
  placeholderImage,
  videoTitle,
  videos,
  video,
  setVideo,
  playing,
  setPlaying,
  duration,
  playedSeconds,
  setPlayedSeconds,
  reverse,
  backgroundColour,
  setApi,
  current,
  count,
  currentHandler,
  handlePlayPause,
  handlePlay,
  handlePause,
  handleProgress,
  handleDuration
}: VideoPlaylistContentProps) => {
  const videoChunks = useMemo(() => chunkVideos(videos, 3), [videos])

  const handleVideoSelect = useCallback(
    (youtubeCode: string) => {
      setVideo(youtubeCode)
      handlePlayPause()
    },
    [setVideo, handlePlayPause]
  )

  return (
    <>
      {(heading || copy) && (
        <div className='space-y-s mb-l'>
          {heading && <h2>{heading}</h2>}
          {copy && (
            <MarkDown content={copy} className='max-w-[85ch] text-pretty' />
          )}
        </div>
      )}
      <div
        className={cn(
          reverse ? 'lg:flex-row-reverse' : 'lg:flex-row',
          'flex flex-col items-center'
        )}
      >
        {/* Aspect ratio may need re-adjusting at some point */}
        <div className='relative w-full max-w-[1015px] transition-all duration-300 md:justify-self-end'>
          {!video && (
            <Image image={placeholderImage} classes='aspect-video w-full' />
          )}
          {video && (
            <ReactPlayer
              url={'https://www.youtube-nocookie.com/embed/' + video}
              playing={playing}
              className='aspect-video w-full'
              width='100%'
              height='100%'
              onReady={() => (console.log('onReady'), setPlayedSeconds(0))}
              onStart={() => console.log('onStart')}
              onPlay={() => (console.log('Video playing'), handlePlay())}
              onPause={() => (console.log('Video paused'), handlePause())}
              onProgress={handleProgress}
              onDuration={handleDuration}
              onEnded={() => (setPlaying(false), setPlayedSeconds(0))}
            />
          )}
        </div>
        <div
          className={cn(
            PlaylistStyles({ backgroundColour: backgroundColour ?? 'white' }),
            reverse ? 'lg:-mr-xl' : 'lg:-ml-xl',
            'space-y-m p-xs-plus max-sm:-mt-xs-plus sm:p-m lg:p-l max-lg:-mt-m z-[1] mx-auto w-[calc(100%-40px)] lg:w-[calc(60%-40px)] lg:max-w-[450px] lg:min-w-[375px]'
            // 'md:my-xl -mt-m'
          )}
        >
          <h3>{videoTitle || 'Playlist'}</h3>

          <Carousel
            setApi={setApi}
            opts={{
              align: 'center',
              loop: false
            }}
            className='space-y-s md:space-y-s'
          >
            <CarouselContent className=''>
              {videoChunks.map((chunk, i) => (
                <CarouselItem
                  key={i}
                  className='space-y-xs md:space-y-m shrink-0'
                >
                  {chunk.map((item, ind) => (
                    <div
                      key={ind}
                      className={cn(
                        !video || video === item.youtubeCode
                          ? 'opacity-100'
                          : 'opacity-75',
                        ['ink-blue', 'mid-blue'].includes(
                          backgroundColour ?? ''
                        )
                          ? 'border-white'
                          : 'border-grey-m',
                        'gap-s pb-xs md:pb-s group relative flex cursor-pointer border-b-2 transition-all duration-300'
                      )}
                      onClick={() => handleVideoSelect(item.youtubeCode)}
                      onMouseEnter={() => {
                        !playing && setVideo(item.youtubeCode)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setVideo(item.youtubeCode)
                          handlePlayPause()
                        }
                      }}
                      role='button'
                      tabIndex={0}
                    >
                      {video === item.youtubeCode ? (
                        !playing ? (
                          <Play
                            fill={''}
                            secondaryfill={undefined}
                            title={''}
                            width={'1.25em'}
                            height={'1.25em'}
                            id={`play-${item.youtubeCode}-${ind}`}
                            className={cn('size-s shrink-0')}
                          />
                        ) : (
                          <MediaPause
                            fill={''}
                            secondaryfill={undefined}
                            title={''}
                            width={'1.25em'}
                            height={'1.25em'}
                            id={`play-${item.youtubeCode}-${ind}`}
                            className={cn('size-s shrink-0')}
                          />
                        )
                      ) : (
                        <Play
                          fill={''}
                          secondaryfill={undefined}
                          title={''}
                          width={'1.25em'}
                          height={'1.25em'}
                          id={`play-${item.youtubeCode}-${ind}`}
                          className={cn('size-s shrink-0')}
                        />
                      )}

                      <div className='space-y-2xs flex flex-col'>
                        <h3 className='text-base-lg font-bold md:line-clamp-1'>
                          {item.heading}
                        </h3>
                        <div className='space-x-xs flex flex-wrap items-center'>
                          <p className='tag text-tag-lg'>
                            {item.category.entryTitle}
                          </p>
                          <span
                            className={cn(
                              ['ink-blue', 'mid-blue'].includes(
                                backgroundColour ?? ''
                              )
                                ? 'bg-white'
                                : 'bg-blue-d',
                              'size-3xs'
                            )}
                          ></span>
                          <p className='tag text-tag-lg'>
                            {item.duration +
                              (Number(item.duration) > 1 ? ' mins' : ' min')}
                          </p>
                        </div>
                      </div>

                      {video === item.youtubeCode && (
                        <>
                          <FormUIProgress
                            value={playedSeconds}
                            max={duration}
                            barColour={'light-blue'}
                            showPercentage={false}
                            className='absolute -bottom-[2px] left-0 h-[2px] w-full bg-transparent transition-all duration-300'
                          />
                        </>
                      )}
                    </div>
                  ))}
                </CarouselItem>
              ))}
            </CarouselContent>

            {videos.length > 3 && (
              <ClientOnly>
                {() => (
                  <CarouselControls
                    current={current}
                    count={count}
                    colour={
                      ['ink-blue', 'mid-blue'].includes(backgroundColour ?? '')
                        ? 'white'
                        : 'ink-blue'
                    }
                    setCurrent={currentHandler}
                  />
                )}
              </ClientOnly>
            )}
          </Carousel>
        </div>
      </div>
    </>
  )
}

const VideoPlaylist = ({
  content,
  layoutColour,
  className
}: VideoPlaylistProps) => {
  const {
    heading,
    copy,
    placeholderImage,
    backgroundColour,
    reverse,
    videoTitle,
    videos = []
  } = content

  const videoIds = videos.map((video: any) => video?.sys?.id).filter(Boolean)

  const { data, isLoading, error } = useQuery({
    queryKey: ['video-playlist'],
    queryFn: async () => {
      return videoIds.length ? getEntriesByIds(videoIds) : []
    },
    enabled: videoIds.length > 0
  })

  const dataById = Array.isArray(data)
    ? data.reduce((acc: Record<string, any>, entry: any) => {
        acc[entry?.sys?.id] = entry
        return acc
      }, {})
    : {}

  const hydratedVideos = Array.isArray(videos)
    ? videos.map((video: any) =>
        video?.sys?.id ? dataById[video.sys.id] || video : video
      )
    : []

  const [video, setVideo] = useState<string>('')
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)

  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState<number>(0)
  const [playedSeconds, setPlayedSeconds] = useState(0)

  useEffect(() => {
    if (!api) {
      return
    }

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  useEffect(() => {
    videos.length > 0 && setVideo(videos[0].youtubeCode)
  }, [videos])

  const currentHandler = (value: number) => {
    setCurrent(value)
    api?.scrollTo(value - 1)
  }

  const handleProgress = (value: Progress) => {
    // console.log('prog', value)
    setPlayedSeconds(value.playedSeconds)
  }

  const handleDuration = (value: number) => {
    // console.log('dur', value)
    setDuration(value)
  }

  const handlePlayPause = () => {
    // console.log('handlePlayPause')
    setPlaying(!playing)
  }

  const handlePlay = () => {
    // console.log('Playing')
    setPlaying(true)
  }

  const handlePause = () => {
    // console.log('Paused')
    setPlaying(false)
  }
  if (isLoading)
    return (
      <div className='py-l flex w-full justify-center'>
        <Loader fill={'#f5b6dc'} />
      </div>
    )
  if (error) return <div>Error loading video.</div>

  if (layoutColour) {
    return (
      <div className={cn('space-y-l lg:space-y-xl', className)}>
        <VideoPlaylistContent
          heading={heading}
          copy={copy}
          placeholderImage={placeholderImage}
          videoTitle={videoTitle}
          videos={
            Array.isArray(hydratedVideos) && hydratedVideos.length
              ? hydratedVideos
              : []
          }
          video={video}
          setVideo={setVideo}
          playing={playing}
          setPlaying={setPlaying}
          duration={duration}
          setDuration={setDuration}
          playedSeconds={playedSeconds}
          setPlayedSeconds={setPlayedSeconds}
          reverse={reverse}
          backgroundColour={backgroundColour}
          setApi={setApi}
          current={current}
          setCurrent={setCurrent}
          count={count}
          currentHandler={currentHandler}
          handlePlayPause={handlePlayPause}
          handlePlay={handlePlay}
          handlePause={handlePause}
          handleProgress={handleProgress}
          handleDuration={handleDuration}
        />
      </div>
    )
  }

  return (
    <Container
      columns={'full'}
      config={{ sectionName: 'Video Playlist', edgetoedgeBackground: true }}
      className={cn(className)}
    >
      <Full>
        <VideoPlaylistContent
          heading={heading}
          copy={copy}
          placeholderImage={placeholderImage}
          videoTitle={videoTitle}
          videos={
            Array.isArray(hydratedVideos) && hydratedVideos.length
              ? hydratedVideos
              : []
          }
          video={video}
          setVideo={setVideo}
          playing={playing}
          setPlaying={setPlaying}
          duration={duration}
          setDuration={setDuration}
          playedSeconds={playedSeconds}
          setPlayedSeconds={setPlayedSeconds}
          reverse={reverse}
          backgroundColour={backgroundColour}
          setApi={setApi}
          current={current}
          setCurrent={setCurrent}
          count={count}
          currentHandler={currentHandler}
          handlePlayPause={handlePlayPause}
          handlePlay={handlePlay}
          handlePause={handlePause}
          handleProgress={handleProgress}
          handleDuration={handleDuration}
        />
      </Full>
    </Container>
  )
}

export default VideoPlaylist
